import type { Profile } from "@fusioncraft/shared";
import { useState } from "react";
import { api } from "../api";
import { Dialog } from "./Dialog";
import { ProfileForm } from "./ProfileForm";

function ProfileRow({
  profile,
  active,
  onSwitch,
  onChanged,
}: {
  profile: Profile;
  active: boolean;
  onSwitch: () => void;
  onChanged: () => Promise<unknown>;
}) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(profile.name);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function act(fn: () => Promise<unknown>) {
    setError(null);
    try {
      await fn();
      await onChanged();
    } catch (err) {
      setError((err as Error).message);
    }
  }

  return (
    <li className={active ? "profile-item is-active" : "profile-item"}>
      <div className="profile-item-main">
        {editing ? (
          <form
            className="inline-form"
            onSubmit={(e) => {
              e.preventDefault();
              act(() => api.renameProfile(profile.id, name)).then(() => setEditing(false));
            }}
          >
            <input className="input input-sm" value={name} maxLength={40} onChange={(e) => setName(e.target.value)} autoFocus />
            <button className="btn btn-sm btn-primary">Save</button>
            <button type="button" className="btn btn-sm" onClick={() => setEditing(false)}>
              Cancel
            </button>
          </form>
        ) : (
          <button className="profile-item-name" onClick={onSwitch} disabled={active}>
            {profile.name}
            {active && <span className="tag">current</span>}
          </button>
        )}
        <div className="profile-item-meta">
          <code>{profile.model}</code>
          <span>{profile.scope === "shared" ? "shared world" : "private world"}</span>
          <span>{profile.elementCount} elements</span>
        </div>
        {error && <p className="form-error">{error}</p>}
      </div>
      {!editing && (
        <div className="profile-item-actions">
          {confirmDelete ? (
            <>
              <span className="muted">Delete for good?</span>
              <button className="btn btn-sm btn-danger" onClick={() => act(() => api.deleteProfile(profile.id))}>
                Delete
              </button>
              <button className="btn btn-sm" onClick={() => setConfirmDelete(false)}>
                Keep
              </button>
            </>
          ) : (
            <>
              <button className="btn btn-sm" onClick={() => setEditing(true)}>
                Rename
              </button>
              <button className="btn btn-sm" onClick={() => setConfirmDelete(true)}>
                Delete
              </button>
            </>
          )}
        </div>
      )}
    </li>
  );
}

export function ProfilesDialog({
  profiles,
  activeId,
  startWithForm,
  onSwitch,
  onChanged,
  onClose,
}: {
  profiles: Profile[];
  activeId: string;
  startWithForm: boolean;
  onSwitch: (id: string) => void;
  onChanged: () => Promise<Profile[]>;
  onClose: () => void;
}) {
  const [creating, setCreating] = useState(startWithForm);

  return (
    <Dialog title={creating ? "New profile" : "Profiles"} onClose={onClose} wide={creating}>
      {creating ? (
        <ProfileForm
          suggestedName={`Profile ${profiles.length + 1}`}
          onCancel={() => setCreating(false)}
          onCreated={async (p) => {
            await onChanged();
            onSwitch(p.id);
            onClose();
          }}
        />
      ) : (
        <>
          <ul className="profile-list">
            {profiles.map((p) => (
              <ProfileRow
                key={p.id}
                profile={p}
                active={p.id === activeId}
                onSwitch={() => {
                  onSwitch(p.id);
                  onClose();
                }}
                onChanged={onChanged}
              />
            ))}
          </ul>
          <div className="form-actions">
            <button className="btn btn-primary" onClick={() => setCreating(true)}>
              New profile
            </button>
          </div>
        </>
      )}
    </Dialog>
  );
}
